/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	Download,
	Wrench,
	HardHat,
	AlertTriangle,
	Phone,
	HelpCircle,
} from "lucide-react"; // Using Wrench or HardHat as main icon
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path

// --- Installation Guide Data ---
// Consider adding a 'shadeType' key if guides correspond to Roller, Zebra etc. to potentially filter later.
const guides = [
	{
		id: "open-roll",
		type: "Open Roll / Reverse Roll",
		desc: "For standard and reverse roll shades without a top cassette or fascia.",
		image: "/images/in/open roll - reverse roll.png", // Ensure this image clearly shows the hardware type
		pdf: "/pdf/Open Roll.pdf", // Ensure PDF names are accurate
	},
	{
		id: "square-cassette",
		type: "Square Cassette",
		desc: "For shades housed within a modern, square-edged top cassette.",
		image: "/images/shadeinfo/valance-options/square cassette.png", // Ensure image shows cassette clearly
		pdf: "/pdf/square.pdf",
	},
	{
		id: "curved-cassette",
		type: "Curved Cassette",
		desc: "For shades housed within a rounded top cassette (Compact or Standard).",
		image: "/images/in/wrapped curved cassette.png", // Ensure image shows cassette clearly
		pdf: "/pdf/Curved Cassettes.pdf",
	},
	{
		id: "motorization",
		type: "Motorized Shades",
		desc: "Installation and programming instructions for rechargeable motorized shades.",
		image: "/images/in/motor.png", // Ensure image shows motor/remote
		pdf: "/pdf/OCS - Specifications & Programming - OCS Rechargeable Motors.pdf",
	},
	// Add guides for Cellular, Sheer etc. if they differ significantly
];

// --- Reusable Guide Card Component ---
const GuideCard = ({ guide, delay }) => {
	return (
		<motion.div
			key={guide.id}
			className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col" // Use flex-col
			initial={{ opacity: 0, y: 30 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.4, delay: delay, ease: "easeOut" }}
			viewport={{ once: true, amount: 0.2 }}
			whileHover={{
				y: -4,
				boxShadow:
					"0 10px 15px -3px rgba(0,0,0,0.07), 0 4px 6px -4px rgba(0,0,0,0.07)",
			}} // Refined hover effect
		>
			{/* Image */}
			<div className="h-40 bg-gray-100 flex items-center justify-center p-4 border-b border-gray-100 overflow-hidden">
				<img
					src={guide.image}
					alt={guide.type}
					className="max-h-full max-w-full object-contain" // Contain ensures visibility
				/>
			</div>
			{/* Content */}
			<div className="p-5 md:p-6 flex flex-col flex-grow">
				<h2 className="text-lg font-semibold text-gray-800 mb-2">
					{guide.type}
				</h2>
				<p className="text-sm text-gray-600 mb-4 flex-grow">{guide.desc}</p>
				{/* Download Button */}
				<a
					href={guide.pdf}
					target="_blank"
					rel="noopener noreferrer" // Security for _blank
					className="mt-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg font-semibold text-sm shadow-sm hover:bg-primary/90 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
				>
					<Download size={16} />
					Download PDF Guide
				</a>
			</div>
		</motion.div>
	);
};

// --- Main Installation Screen ---
const InstallationScreen = () => {
	return (
		<div className="overflow-x-hidden bg-gray-50">
			<Helmet>
				<title>Installation Guides | Off Cut Shades</title>{" "}
				{/* Simplified Title */}
				<meta
					name="description"
					content="Easy step-by-step guides to install your Off Cut Shades Roller, Zebra, Cassette, or Motorized blinds. Download PDF instructions."
				/>{" "}
				{/* Action-oriented Desc */}
				<link
					rel="canonical"
					href="https://offcutshades.com/guides/installation-guide"
				/>{" "}
				{/* Canonical URL */}
			</Helmet>

			<main className="pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding for fixed header */}
				{/* --- Hero Section --- */}
				<section className="container mx-auto px-4 text-center mb-12 md:mb-16">
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
						className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
					>
						<Wrench size={36} strokeWidth={1.5} /> {/* Using Lucide Icon */}
					</motion.div>
					<motion.h1
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
					>
						Installation Guides
					</motion.h1>
					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="text-lg text-gray-600 max-w-2xl mx-auto"
					>
						You're almost there! Follow our simple guides to install your
						beautiful new shades quickly and easily.
					</motion.p>
				</section>
				{/* --- Before You Start Section --- */}
				<section className="container mx-auto px-4 max-w-4xl mx-auto mb-12 md:mb-16">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
					>
						<h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
							<HardHat size={20} className="text-primary" /> Before You Begin
						</h2>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
							<div className="space-y-3">
								<h3 className="font-medium text-gray-700">
									Check Your Package:
								</h3>
								<ul className="list-disc list-outside pl-5 space-y-1 text-gray-600">
									<li>Ensure all parts listed in the guide are included.</li>
									<li>Verify shade dimensions match your order/window.</li>
								</ul>
							</div>
							<div className="space-y-3">
								<h3 className="font-medium text-gray-700">Tools Needed:</h3>
								<ul className="list-disc list-outside pl-5 space-y-1 text-gray-600">
									<li>Metal Tape Measure</li>
									<li>Pencil</li>
									<li>Drill with appropriate bits</li>
									<li>Screwdriver (usually Phillips)</li>
									<li>Level (Recommended)</li>
									<li>Step Ladder (If needed)</li>
								</ul>
							</div>
						</div>
						<div className="mt-5 bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-lg text-xs flex items-start gap-2">
							<AlertTriangle
								size={16}
								className="mt-0.5 text-yellow-600 flex-shrink-0"
							/>
							<span>
								Read the entire guide for your specific product *before*
								starting installation. If unsure, contact us!
							</span>
						</div>
					</motion.div>
				</section>
				{/* --- Installation Guides Grid --- */}
				<section className="container mx-auto px-4 max-w-5xl mx-auto">
					<h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6 md:mb-8 text-center md:text-left">
						Find Your Guide
					</h2>
					{/* Adjust grid columns for desired layout */}
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
						{guides.map((guide, index) => (
							<GuideCard key={guide.id} guide={guide} delay={index * 0.1} />
						))}
						{/* Add more cards if needed */}
					</div>
				</section>
			</main>

			{/* --- Contact Support Section --- */}
			<ContactBlock
				title="Need Installation Help?"
				description="Our support team is ready to assist you with any questions during the installation process. Don't hesitate to reach out!"
			/>
		</div>
	);
};

export default InstallationScreen;
